import React from "react";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLocation as useRouterLocation, useNavigate } from "react-router-dom";
import { HomeSizeGuideModal } from "./HomeSizeGuideModal";
import { SaveQuoteModal } from "./SaveQuoteModal";
import { getPricesBySqft } from "@/lib/pricing";
import { useCalculatorState } from "@/hooks/use-calculator-state";
import { RotateCcw, Calendar } from "lucide-react";

export const PricingCalculator = ({ embedded = false }: { embedded?: boolean }) => {
  const { state: calc, update, reset } = useCalculatorState();
  const sqft = [calc.sqft];
  const setSqft = (val: number[]) => update({ sqft: val[0] });
  const bedrooms = calc.bedrooms;
  const setBedrooms = (n: number) => update({ bedrooms: n });
  const bathrooms = calc.bathrooms;
  const setBathrooms = (n: number) => update({ bathrooms: n });
  // PricingCalculator uses "bi-weekly" format internally; map to/from shared key
  const freqMap: Record<string, string> = { onetime: "one-time", weekly: "weekly", biweekly: "bi-weekly", triweekly: "tri-weekly", monthly: "monthly" };
  const freqReverseMap: Record<string, string> = { "one-time": "onetime", "weekly": "weekly", "bi-weekly": "biweekly", "tri-weekly": "triweekly", "monthly": "monthly" };
  const frequency = freqMap[calc.frequency] ?? "bi-weekly";
  const setFrequency = (f: string) => update({ frequency: (freqReverseMap[f] ?? "biweekly") as any });
  const serviceType = (calc.serviceType === "construction" ? "standard" : calc.serviceType) as "standard" | "deep" | "move";
  const setServiceType = (s: "standard" | "deep" | "move") => update({ serviceType: s });
  const { pathname } = useRouterLocation();
  const navigate = useNavigate();

  const isEs = pathname.startsWith('/es');
  const isRu = pathname.startsWith('/ru');
  const isZh = pathname.startsWith('/zh');

  const t = (en: string, es: string, ru: string, zh: string) =>
    isEs ? es : isRu ? ru : isZh ? zh : en;

  const prices = getPricesBySqft(sqft[0]);
  const basePrice = prices[serviceType];

  const isRecurring = frequency !== "one-time";

  let multiplier = 1;
  if (frequency === "weekly") multiplier = 0.65;
  else if (frequency === "bi-weekly") multiplier = 0.70;
  else if (frequency === "tri-weekly") multiplier = 0.75;
  else if (frequency === "monthly") multiplier = 0.80;

  const firstVisitPrice = basePrice;
  const recurringPrice = Math.round(basePrice * multiplier);
  const finalPrice = recurringPrice;

  const freqOptions = [
    { id: "weekly",     label: t("Weekly (-35%)",    "Semanal (-35%)",    "Еженедельно (-35%)", "每周 (-35%)") },
    { id: "bi-weekly",  label: t("Bi-Weekly (-30%)", "Quincenal (-30%)",  "Раз в 2 нед. (-30%)","每两周 (-30%)") },
    { id: "tri-weekly", label: t("Every 3 Weeks (-25%)", "Cada 3 Semanas (-25%)", "Каждые 3 недели (-25%)", "每三周 (-25%)") },
    { id: "monthly",    label: t("Monthly (-20%)",   "Mensual (-20%)",    "Ежемесячно (-20%)",  "每月 (-20%)") },
    { id: "one-time",   label: t("One-Time",         "Una Vez",           "Разовая",            "单次") },
  ];

  const serviceOptions = [
    { id: "standard", label: t("Standard Clean", "Limpieza Estándar", "Стандартная уборка", "标准清洁") },
    { id: "deep", label: t("Deep Clean", "Limpieza Profunda", "Генеральная уборка", "深度清洁") },
    { id: "move", label: t("Move In/Out", "Mudanza", "Переезд", "搬家清洁") },
  ];

  return (
    <section className={embedded ? "py-8 bg-background" : "py-24 bg-background"}>
      <div className="container max-w-4xl">
        {!embedded && (
          <div className="text-center mb-12 relative">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              {t("Instant Pricing Calculator", "Calculadora de Precios Instantánea", "Мгновенный расчёт цены", "即时报价计算器")}
            </h2>
            <p className="text-lg text-muted-foreground">
              {t("Get an immediate estimate for your home. No hidden fees or surprises.", "Obtén un presupuesto inmediato para tu hogar. Sin cargos ocultos ni sorpresas.", "Получите немедленную оценку стоимости. Без скрытых платежей и сюрпризов.", "立即获取您家的清洁报价。无隐藏费用，无意外收费。")}
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4 gap-2 rounded-full border-primary/20 text-primary hover:bg-primary/5"
              onClick={reset}
            >
              <RotateCcw className="w-4 h-4" />
              {t("Reset All Calculators", "Reiniciar Calculadoras", "Сбросить расчеты", "重置计算器")}
            </Button>
          </div>
        )}

        <Card className="border-2 border-primary/20 shadow-lg">
          <CardContent className="p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="space-y-8">
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="text-lg font-semibold">{t("Square Footage", "Pies Cuadrados", "Квадратные футы", "平方英尺")}</label>
                    <span className="text-2xl font-bold text-primary">{sqft[0] > 5500 ? "5500+" : sqft[0]} sq ft</span>
                  </div>
                  <Slider defaultValue={[0]} max={6000} min={0} step={100} value={sqft} aria-label="Square footage" onValueChange={setSqft} className="py-4 cursor-pointer" />
                  <div className="flex items-center justify-between mt-1">
                    <HomeSizeGuideModal triggerText={t("Size Guide", "Guía de Tamaño", "Гид по размерам", "尺寸指南")} />
                    <a href="https://www.zillow.com/" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-primary bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-md hover:bg-primary/20 transition-colors flex items-center justify-center gap-1.5 w-fit shadow-sm">
                      {t("Unsure? Look up on Zillow ↗", "¿Inseguro? Buscar en Zillow ↗", "Не уверены? Найти на Zillow ↗", "不确定？在 Zillow 上查找 ↗")}
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="text-sm font-semibold block text-muted-foreground">{t("Bedrooms", "Habitaciones", "Спальни", "卧室")}</label>
                    <div className="flex items-center justify-between bg-muted/30 rounded-lg p-2 border">
                      <button onClick={() => setBedrooms(Math.max(0, bedrooms - 1))} className="w-8 h-8 rounded-md bg-background border flex items-center justify-center hover:bg-muted">-</button>
                      <span className="font-bold">{bedrooms}</span>
                      <button onClick={() => setBedrooms(Math.min(10, bedrooms + 1))} className="w-8 h-8 rounded-md bg-background border flex items-center justify-center hover:bg-muted">+</button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-sm font-semibold block text-muted-foreground">{t("Bathrooms", "Baños", "Ванные", "浴室")}</label>
                    <div className="flex items-center justify-between bg-muted/30 rounded-lg p-2 border">
                      <button onClick={() => setBathrooms(Math.max(0, bathrooms - 1))} className="w-8 h-8 rounded-md bg-background border flex items-center justify-center hover:bg-muted">-</button>
                      <span className="font-bold">{bathrooms}</span>
                      <button onClick={() => setBathrooms(Math.min(10, bathrooms + 1))} className="w-8 h-8 rounded-md bg-background border flex items-center justify-center hover:bg-muted">+</button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-lg font-semibold block">{t("Service Type", "Tipo de Servicio", "Тип уборки", "服务类型")}</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {serviceOptions.map((service) => (
                      <Button key={service.id} variant={serviceType === service.id ? "default" : "outline"} className={`w-full ${serviceType === service.id ? "bg-primary text-primary-foreground font-bold border-primary" : "text-muted-foreground hover:text-foreground"}`} onClick={() => setServiceType(service.id as any)}>
                        {service.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-lg font-semibold block">{t("Frequency", "Frecuencia", "Частота", "频率")}</label>
                  <div className="grid grid-cols-2 gap-3">
                    {freqOptions.map((freq) => (
                      <Button key={freq.id} variant={frequency === freq.id ? "default" : "outline"} className={`w-full ${frequency === freq.id ? "bg-primary text-primary-foreground font-bold border-primary" : "text-muted-foreground hover:text-foreground"}`} onClick={() => setFrequency(freq.id)}>
                        {freq.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col justify-center items-center p-8 bg-muted/30 rounded-xl text-center border border-border/50">
                {sqft[0] > 5500 ? (
                  <div className="py-8">
                    <h3 className="text-2xl font-bold mb-4">{t("Custom Quote Required", "Cotización Personalizada", "Требуется индивидуальный расчет", "需要定制报价")}</h3>
                    <p className="text-muted-foreground mb-6">{t("For homes over 5,500 sq ft, please call or email us for a custom quote to ensure accurate pricing.", "Para hogares de más de 5,500 pies cuadrados, llámenos o envíenos un correo electrónico para obtener una cotización personalizada.", "Для домов площадью более 5500 кв. футов, пожалуйста, позвоните или напишите нам для индивидуального расчета.", "对于超过 5,500 平方英尺的房屋，请致电或发电子邮件给我们以获取定制报价。")}</p>
                    <div className="flex flex-col gap-3 w-full">
                      <Button asChild size="lg" className="w-full font-bold"><a href="tel:+19162995588">{t("Call Us", "Llámanos", "Позвонить", "致电我们")}</a></Button>
                      <Button asChild variant="outline" size="lg" className="w-full font-bold"><a href="mailto:support@uplevelcleaning.com">{t("Email Us", "Envíanos un correo", "Написать", "发送邮件")}</a></Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-medium text-muted-foreground mb-2">{t("Estimated Price", "Precio Estimado", "Оценочная стоимость", "预估价格")}</h3>
                    {isRecurring ? (
                      <div className="w-full mb-4 space-y-3">
                        <div className="bg-muted/40 border border-border rounded-lg p-3 text-left">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{t("1st Visit (Full Price)", "1.ª Visita (Precio Completo)", "1-й визит (полная цена)", "首次上门（全价）")}</p>
                          <p className="text-3xl font-bold text-foreground">${firstVisitPrice}</p>
                        </div>
                        <div className="bg-primary/5 border border-primary/30 rounded-lg p-3 text-left">
                          <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">{t("Subsequent Visits (Discounted)", "Visitas Siguientes (Descuento)", "Следующие визиты (со скидкой)", "后续上门（折扣价）")}</p>
                          <p className="text-3xl font-bold text-primary">${recurringPrice}</p>
                        </div>
                        <p className="text-[11px] text-muted-foreground bg-muted/30 rounded-md px-3 py-2 leading-snug">{t("Your first visit is charged at full price. The recurring discount applies from your 2nd appointment onward.", "Tu primera visita se cobra a precio completo. El descuento por recurrencia aplica a partir de la 2.ª cita.", "Первый визит оплачивается по полной цене. Скидка на регулярные уборки применяется со 2-го визита.", "首次上门按全价收费，从第2次预约起享受定期优惠折扣。")}</p>
                      </div>
                    ) : <div className="text-6xl font-bold text-primary mb-4">${finalPrice}</div>}
                    <p className="text-sm text-muted-foreground mb-6">{t(`Based on ${sqft[0]} sq ft ${serviceType} clean.`, `Basado en limpieza de ${sqft[0]} pies cuadrados.`, `На основе уборки ${sqft[0]} кв. футов.`, `基于 ${sqft[0]} 平方英尺清洁。`)}</p>
                    <Button size="lg" className="w-full text-lg h-14 font-bold" onClick={() => {
                      const planDetails = { serviceType, sqft: sqft[0], bedrooms, bathrooms, frequency: frequency === "one-time" ? "onetime" : frequency.replace("-", ""), addons: [], totalPrice: finalPrice };
                      localStorage.setItem('uplevel_saved_plan', JSON.stringify(planDetails));
                      navigate('/book?view=booking');
                    }}>
                      <Calendar className="mr-2 h-5 w-5" />
                      {t("Book Now", "Reservar Ahora", "Записаться", "立即预约")}
                    </Button>
                    <div className="w-full mt-4"><SaveQuoteModal sqft={sqft[0]} frequency={frequency} price={finalPrice} triggerText={t("Save Quote for Later", "Guardar Presupuesto", "Сохранить расчет", "保存报价")} /></div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
